/**
 * English — the source of truth for translation keys.
 *
 * `TranslationKey` is derived from this object, and every other locale is typed
 * as `Translations`, so a missing or misspelled key is a TypeScript error
 * rather than a silent fallback at runtime.
 *
 * Placeholders use `{name}` syntax and are substituted by `t(key, params)`.
 */
export const en = {
  'app.name': 'ITA6',
  'app.tagline': 'Skill-based online contests',
  'app.skipToContent': 'Skip to main content',

  'nav.primary': 'Primary',
  'nav.openMenu': 'Open menu',
  'nav.closeMenu': 'Close menu',
  'nav.home': 'Home',
  'nav.dashboard': 'Dashboard',
  'nav.profile': 'Profile',
  'nav.admin': 'Admin',
  'nav.signIn': 'Sign in',
  'nav.signOut': 'Sign out',
  'nav.createAccount': 'Create account',

  'role.user': 'Participant',
  'role.admin': 'Administrator',
  'role.userShort': 'user',
  'role.adminShort': 'admin',

  'status.active': 'active',
  'status.suspended': 'suspended',
  'status.deleted': 'deleted',
  'status.verified': 'verified',
  'status.notVerified': 'not verified',

  'common.loading': 'Loading…',
  'common.refresh': 'Refresh',
  'common.yes': 'Yes',
  'common.no': 'No',
  'common.backToHome': 'Back to home',
  'common.backToDashboard': 'Back to dashboard',
  'common.backToContest': 'Back to contest',
  'common.notAvailable': 'Not available',

  'theme.label': 'Theme',
  'theme.light': 'Light',
  'theme.dark': 'Dark',
  'theme.system': 'System',

  'language.label': 'Language',

  'home.hero.title': 'Prove your skill.',
  'home.hero.titleAccent': 'Win on merit.',
  'home.hero.subtitle':
    'ITA6 is a skill-based contest platform. Enter talent-based contests, complete timed objective challenges, and let documented scoring rules — not chance — decide the winners.',
  'home.hero.goToDashboard': 'Go to dashboard',
  'home.hero.createAccount': 'Create your account',
  'home.hero.signIn': 'Sign in',
  'home.highlights.title': 'What ITA6 offers',
  'home.highlights.contests': 'Browse contests and see how many people have entered',
  'home.highlights.prizes': 'Transparent prize pools funded by entry fees',
  'home.highlights.timed': 'Timed objective challenges with fair, platform-verified scoring',
  'home.highlights.results': 'Tie-aware leaderboards and published results',
  'home.highlights.admin': 'Role-based administration for contests, users and payouts',
  'home.highlights.note':
    'Contest entry opens shortly. Create your account now and you will be ready on day one.',
  'home.features.title': 'Built around fairness',
  'home.features.subtitle':
    'Every design decision serves one goal: the most skilful participant should win.',
  'home.features.skill.title': 'Skill, not luck',
  'home.features.skill.body':
    'Contests are decided by objective performance. Winners come from documented scoring rules — never a random draw.',
  'home.features.scoring.title': 'Objective scoring',
  'home.features.scoring.body':
    'Answers are graded and timed by the platform itself, so a score cannot be edited from a browser.',
  'home.features.standing.title': 'Transparent standings',
  'home.features.standing.body':
    'Participant counts, prize pools and leaderboards are visible to every entrant, before and after the result.',
  'home.features.accounts.title': 'One account, every contest',
  'home.features.accounts.body':
    'A single verified profile tracks your entries, results and winnings across the whole platform.',
  'home.steps.title': 'How a contest works',
  'home.steps.one.title': 'Create your profile',
  'home.steps.one.body': 'Register once and you are ready for every contest on the platform.',
  'home.steps.two.title': 'Enter a contest',
  'home.steps.two.body': 'Pay the entry fee for a skill contest that matches your interests.',
  'home.steps.three.title': 'Take the challenge',
  'home.steps.three.body':
    'Answer timed objective questions under fair, platform-enforced timing.',
  'home.steps.four.title': 'See where you rank',
  'home.steps.four.body':
    'Earn an objective score and climb a transparent, tie-aware leaderboard.',
  'home.cta.title': 'Ready to test your skill?',
  'home.cta.body': 'Create a free account and be ready when the first contests open.',
  'home.cta.button': 'Get started',

  'login.title': 'Welcome back',
  'login.subtitle': 'Sign in to your ITA6 account.',
  'login.cardTitle': 'Sign in',
  'login.cardDescription': 'Use the email and password you registered with.',
  'login.email': 'Email',
  'login.emailPlaceholder': 'you@example.com',
  'login.password': 'Password',
  'login.passwordPlaceholder': 'Your password',
  'login.submit': 'Sign in',
  'login.noAccount': 'Don’t have an account?',
  'login.createOne': 'Create one',
  'login.errorRequired': 'Enter your email and password.',
  'login.errorGeneric': 'Sign in failed. Please try again.',

  'register.title': 'Create your account',
  'register.subtitle': 'One account for every ITA6 contest. It’s free to join.',
  'register.cardTitle': 'Register',
  'register.cardDescription': 'All fields except display name are required.',
  'register.displayName': 'Display name (optional)',
  'register.displayNamePlaceholder': 'How you appear on leaderboards',
  'register.email': 'Email',
  'register.password': 'Password',
  'register.passwordPlaceholder': 'At least {min} characters',
  'register.confirmPassword': 'Confirm password',
  'register.confirmPlaceholder': 'Repeat your password',
  'register.submit': 'Create account',
  'register.securityNote': 'Your password is stored securely using industry-standard hashing.',
  'register.haveAccount': 'Already have an account?',
  'register.signIn': 'Sign in',
  'register.errorGeneric': 'Registration failed. Please try again.',

  'validation.emailRequired': 'Email is required.',
  'validation.emailInvalid': 'Enter a valid email address.',
  'validation.passwordMin': 'Password must be at least {min} characters.',
  'validation.passwordComplexity': 'Password must contain at least one letter and one number.',
  'validation.passwordMismatch': 'Passwords do not match.',

  'dashboard.title': 'Welcome, {name}',
  'dashboard.subtitle': 'This is your ITA6 dashboard. Your contests and results will appear here.',
  'dashboard.adminConsole': 'Admin console',
  'dashboard.viewProfile': 'View profile',
  'dashboard.stat.displayName': 'Display name',
  'dashboard.stat.email': 'Email',
  'dashboard.stat.role': 'Role',
  'dashboard.stat.memberSince': 'Member since',
  'dashboard.stat.lastLogin': 'Last login: {date}',
  'dashboard.roleHint.admin': 'Full administrative access',
  'dashboard.roleHint.user': 'Participant access',
  'dashboard.contests.title': 'Your contests',
  'dashboard.contests.description':
    'Contests you enter will be listed here with their status and results.',
  'dashboard.contests.empty.title': 'No contests yet',
  'dashboard.contests.empty.body':
    'Contest entry opens shortly. Once you enter a contest it will appear here.',
  'dashboard.account.title': 'Account status',
  'dashboard.account.description': 'How your account currently stands.',
  'dashboard.account.status': 'Status',
  'dashboard.account.emailVerified': 'Email verified',
  'dashboard.account.role': 'Role',
  'dashboard.account.id': 'Account ID',

  'profile.title': 'Your profile',
  'profile.subtitle': 'This is the information other participants see alongside your results.',
  'profile.details.title': 'Profile details',
  'profile.details.description': 'Public information shown alongside your results.',
  'profile.field.displayName': 'Display name',
  'profile.field.country': 'Country',
  'profile.field.bio': 'Bio',
  'profile.account.title': 'Account details',
  'profile.account.description': 'Your private account information.',
  'profile.field.email': 'Email',
  'profile.field.emailVerified': 'Email verified',
  'profile.field.role': 'Role',
  'profile.field.status': 'Status',
  'profile.field.memberSince': 'Member since',
  'profile.field.lastLogin': 'Last login',
  'profile.field.accountId': 'Account ID',

  'admin.title': 'Admin console',
  'admin.subtitle': 'Platform status and administrative access.',
  'admin.refresh': 'Refresh',
  'admin.loading': 'Loading admin console…',
  'admin.errorLoad': 'Failed to load platform status.',
  'admin.signedInAs': 'Signed in as',
  'admin.users': 'Users',
  'admin.usersSummary': '{admins} administrators · {active} active',
  'admin.apiLabel': 'Platform API',
  'admin.apiHealthy': 'operational',
  'admin.apiUnavailable': 'unavailable',
  'admin.database': 'Database',
  'admin.databaseOk': 'connected',
  'admin.databaseUnavailable': 'unavailable',
  'admin.databaseUnknown': 'unknown',
  'admin.serverTime': 'Server time: {date}',
  'admin.session.title': 'Session',
  'admin.session.description': 'Your authenticated administrator context.',
  'admin.field.email': 'Email',
  'admin.field.role': 'Role',
  'admin.field.accountId': 'Account ID',
  'admin.field.statusChecked': 'Status checked',
  'admin.roadmap.title': 'Administration',
  'admin.roadmap.description': 'What you will manage from this console.',
  'admin.roadmap.contests': 'Create and configure contests and challenges',
  'admin.roadmap.questions': 'Author questions and manage answer keys',
  'admin.roadmap.payments': 'Review entry payments and participant lists',
  'admin.roadmap.results': 'Publish results, leaderboards and prize payouts',
  'admin.roadmap.users': 'Moderate users and manage platform settings',

  'guard.checkingSession': 'Checking your session…',
  'guard.accessDenied.title': 'Access denied',
  'guard.accessDenied.body':
    'This area is restricted to administrators. If you believe you should have access, contact the platform owner.',

  'notFound.title': 'Page not found',
  'notFound.body': 'The page you were looking for doesn’t exist or may have moved.',

  'api.networkError': 'Could not reach ITA6. Check your connection and try again.',
  'api.requestFailed': 'Something went wrong. Please try again.',
  'api.invalidCredentials': 'Invalid email or password.',
  'api.emailTaken': 'An account with this email already exists.',
  'api.accountInactive': 'This account is not active.',
  'api.sessionExpired': 'Your session has expired. Please sign in again.',
  'api.unauthorized': 'Please sign in to continue.',
  'api.forbidden': 'You do not have permission to do that.',
  'api.invalidEmail': 'Enter a valid email address.',
  'api.weakPassword': 'Password does not meet the requirements.',
  'api.internalError': 'Something went wrong on our side. Please try again.',

  'footer.copyright': '© {year} ITA6. All rights reserved.',
  'footer.legal': 'Legal',
  'footer.terms': 'Terms',
  'footer.privacy': 'Privacy',
  'footer.contact': 'Contact',

  // --- Phase 2: the contest world -----------------------------------------
  'nav.contests': 'Contests',
  'nav.myEntries': 'My entries',
  'nav.adminContests': 'Contest admin',
  'nav.adminPayments': 'Payments',
  'nav.adminCrypto': 'Crypto',
  'common.save': 'Save',
  'common.cancel': 'Cancel',
  'common.close': 'Close',
  'common.confirm': 'Confirm',
  'common.actions': 'Actions',
  'common.status': 'Status',
  'common.amount': 'Amount',
  'common.date': 'Date',
  'common.none': 'None',
  'common.submit': 'Submit',
  'common.details': 'Details',
  'common.optional': 'optional',
  'common.rail': 'Rail',
  'common.asset': 'Asset',

  'rail.all': 'All contests',
  'rail.fiat': 'Fiat',
  'rail.crypto': 'Crypto',

  'contest.phase.draft': 'Draft',
  'contest.phase.scheduled': 'Scheduled',
  'contest.phase.registration_open': 'Registration open',
  'contest.phase.registration_closed': 'Registration closed',
  'contest.phase.live': 'Live now',
  'contest.phase.awaiting_results': 'Awaiting results',
  'contest.phase.results_ready': 'Results available',
  'contest.phase.cancelled': 'Cancelled',
  'contest.phase.voided': 'Voided',

  'participant.status.registered': 'Registered',
  'participant.status.awaiting_payment': 'Awaiting payment',
  'participant.status.confirmed': 'Confirmed',
  'participant.status.active': 'Playing',
  'participant.status.completed': 'Finished',
  'participant.status.disqualified': 'Disqualified',
  'participant.status.withdrawn': 'Withdrawn',
  'participant.status.refunded': 'Refunded',

  'payment.status.pending': 'Awaiting payment',
  'payment.status.confirmed': 'Paid',
  'payment.status.failed': 'Failed',
  'payment.status.expired': 'Expired',
  'payment.status.refunded': 'Refunded',
  'payment.status.cancelled': 'Cancelled',

  'payout.status.pending': 'Pending',
  'payout.status.approved': 'Approved',
  'payout.status.processing': 'Processing',
  'payout.status.completed': 'Paid',
  'payout.status.failed': 'Failed',
  'payout.status.cancelled': 'Cancelled',
  'payout.kind.prize': 'Prize',
  'payout.kind.refund': 'Refund',

  'contest.list.title': 'Contests',
  'contest.list.subtitle': 'Browse open contests, see the prize pool, and enter.',
  'contest.list.empty': 'No contests are open right now. Check back soon.',
  'contest.list.loadError': 'Could not load contests.',
  'contest.list.view': 'View contest',

  'contest.card.entry': 'Entry fee',
  'contest.card.free': 'Free',
  'contest.card.prizePool': 'Prize pool',
  'contest.card.participants': '{count} entered',
  'contest.card.startsAt': 'Starts {date}',
  'contest.card.endsAt': 'Ends {date}',

  'contest.countdown.opens': 'Registration opens in',
  'contest.countdown.closes': 'Registration closes in',
  'contest.countdown.starts': 'Starts in',
  'contest.countdown.ends': 'Ends in',
  'contest.countdown.past': 'Closed',

  'contest.detail.notFound': 'This contest could not be found.',
  'contest.detail.loadError': 'Could not load this contest.',
  'contest.detail.about': 'About this contest',
  'contest.detail.rules': 'Rules',
  'contest.detail.prizePool': 'Prize pool',
  'contest.detail.entryFee': 'Entry fee',
  'contest.detail.asset': 'Asset',
  'contest.detail.paidPlaces': 'Paid places',
  'contest.detail.minParticipants': 'Minimum entries',
  'contest.detail.questions': 'Questions',
  'contest.detail.secondsPerQuestion': 'Seconds per question',
  'contest.detail.tieBreak': 'Tie-break',
  'contest.detail.signInToEnter': 'Sign in to enter',
  'contest.detail.register': 'Enter contest',
  'contest.detail.registering': 'Entering…',
  'contest.detail.registered': 'You are entered',
  'contest.detail.start': 'Start contest',
  'contest.detail.continue': 'Continue contest',
  'contest.detail.viewResults': 'View results',
  'contest.detail.payNow': 'Open payment page',
  'contest.detail.paymentPending': 'Your entry is waiting for payment to be confirmed.',
  'contest.detail.paymentConfirmed': 'Your payment is confirmed.',
  'contest.detail.paymentRequired': 'This contest has an entry fee.',
  'contest.detail.providerNotConfigured':
    'Online payment is not available yet. Contact the organiser, who can confirm your entry manually.',
  'contest.detail.enterError': 'Could not enter this contest.',
  'contest.detail.cancelled': 'This contest was cancelled.',
  'contest.detail.voided': 'This contest was voided and entry fees are refunded.',

  'contest.play.title': 'Contest in progress',
  'contest.play.questionProgress': 'Question {current} of {total}',
  'contest.play.timeLeft': 'Time left',
  'contest.play.submit': 'Submit answer',
  'contest.play.submitting': 'Submitting…',
  'contest.play.submitted': 'Answer recorded',
  'contest.play.answered': 'Answered',
  'contest.play.numericPlaceholder': 'Enter a number, e.g. 12.5',
  'contest.play.multiHint': 'Select every correct option. Partial answers score zero.',
  'contest.play.next': 'Next question',
  'contest.play.previous': 'Previous',
  'contest.play.finish': 'Finish contest',
  'contest.play.finishing': 'Finishing…',
  'contest.play.noAnswers':
    'You have not submitted any answers yet. Finishing now will end your attempt.',
  'contest.play.error': 'That answer could not be recorded.',
  'contest.play.loadError': 'Could not load the contest.',
  'contest.play.notStarted': 'Start the contest from its page first.',
  'contest.play.finishedTitle': 'Contest finished',
  'contest.play.yourScore': 'Your score',
  'contest.play.correctAnswers': 'Correct answers',
  'contest.play.answeredCount': 'Answers recorded',
  'contest.play.timeUsed': 'Time used',
  'contest.play.meanwhile': 'Final standings appear once the organiser publishes them.',

  'contest.results.title': 'Results',
  'contest.results.notPublished': 'Results for this contest have not been published yet.',
  'contest.results.empty': 'No results to show.',
  'contest.results.loadError': 'Could not load the results.',
  'contest.results.winners': 'Winners',
  'contest.results.leaderboard': 'Final standings',
  'contest.results.rank': 'Rank',
  'contest.results.participant': 'Participant',
  'contest.results.score': 'Score',
  'contest.results.correct': 'Correct',
  'contest.results.time': 'Time',
  'contest.results.prize': 'Prize',
  'contest.results.export': 'Export CSV',
  'contest.results.review': 'Review the answers',
  'contest.results.correctAnswer': 'Correct answer',
  'contest.results.explanation': 'Explanation',

  'entries.title': 'My entries',
  'entries.subtitle': 'Every contest you have entered, with your result and winnings.',
  'entries.empty': 'You have not entered a contest yet.',
  'entries.loadError': 'Could not load your entries.',
  'entries.contest': 'Contest',
  'entries.viewContest': 'Open contest',
  'entries.noResult': 'No result yet',
  'entries.myPayouts': 'My payouts',
  'entries.payoutsEmpty': 'You have no payouts yet.',

  'admin.queue.title': 'Operations',
  'admin.queue.description': 'Contests, entry payments and money owed.',
  'admin.queue.payments': 'Payments awaiting action',
  'admin.queue.payouts': 'Payouts awaiting action',
  'admin.queue.reconciliation': 'Flagged for reconciliation',

  'admin.contests.title': 'Contests',
  'admin.contests.subtitle': 'Create contests, author questions and run the lifecycle.',
  'admin.contests.create': 'New contest',
  'admin.contests.empty': 'No contests yet.',
  'admin.contests.loadError': 'Could not load contests.',
  'admin.contests.formHeading': 'Create a contest',
  'admin.contests.formHint':
    'Entry fee, asset and timing are fixed once the contest leaves draft, so check them carefully.',
  'admin.contests.field.title': 'Title',
  'admin.contests.field.summary': 'Short summary',
  'admin.contests.field.description': 'Description',
  'admin.contests.field.rules': 'Rules',
  'admin.contests.field.rail': 'Payment rail',
  'admin.contests.field.asset': 'Asset',
  'admin.contests.field.entryFee': 'Entry fee (e.g. 10.00)',
  'admin.contests.field.sponsor': 'Sponsor funding (optional)',
  'admin.contests.field.feeBps': 'Platform fee (basis points)',
  'admin.contests.field.minParticipants': 'Minimum entries',
  'admin.contests.field.maxParticipants': 'Maximum entries',
  'admin.contests.field.paidPlaces': 'Paid places',
  'admin.contests.field.regOpens': 'Registration opens',
  'admin.contests.field.regCloses': 'Registration closes',
  'admin.contests.field.startsAt': 'Contest starts',
  'admin.contests.field.endsAt': 'Contest ends',
  'admin.contests.field.perQuestionSeconds': 'Seconds per question (0 = whole window)',
  'admin.contests.field.tieBreak': 'Tie-break',
  'admin.contests.tieBreak.total_time': 'Total answering time',
  'admin.contests.tieBreak.earliest_completion': 'Earliest finish',
  'admin.contests.created': 'Contest created as a draft.',
  'admin.contests.prizePoolLabel': 'Prize pool',
  'admin.contests.platformFeeLabel': 'Platform fee',
  'admin.contests.noPublicPage': 'No public page',
  'admin.contests.questions': 'Questions',
  'admin.contests.noQuestions': 'No questions yet.',
  'admin.contests.addQuestion': 'Add question',
  'admin.contests.questionKind': 'Question type',
  'admin.contests.kind.single_choice': 'One correct answer',
  'admin.contests.kind.multi_choice': 'Several correct answers',
  'admin.contests.kind.true_false': 'True or false',
  'admin.contests.kind.numeric': 'Numeric answer',
  'admin.contests.prompt': 'Question',
  'admin.contests.points': 'Points',
  'admin.contests.options': 'Options',
  'admin.contests.optionLabel': 'Option {n}',
  'admin.contests.correct': 'Correct',
  'admin.contests.numericAnswer': 'Correct number',
  'admin.contests.numericTolerance': 'Accepted tolerance',
  'admin.contests.hintLabel': 'Hint (optional)',
  'admin.contests.explanationLabel': 'Explanation (shown with results)',
  'admin.contests.saveQuestion': 'Save question',
  'admin.contests.deleteQuestion': 'Delete question',
  'admin.contests.answerKey': 'Answer key',
  'admin.contests.unkeyed':
    'Some questions have no correct answer set. Fix them before scheduling.',
  'admin.contests.transitions': 'Lifecycle',
  'admin.contests.action.schedule': 'Schedule contest',
  'admin.contests.action.finalize': 'Finalize results',
  'admin.contests.action.publish': 'Publish results',
  'admin.contests.action.cancel': 'Cancel contest',
  'admin.contests.action.void': 'Void and refund',
  'admin.contests.cancelReason': 'Reason (recorded)',
  'admin.contests.participants': 'Participants',
  'admin.contests.disqualify': 'Disqualify',
  'admin.contests.disqualifyReason': 'Reason for disqualification',
  'admin.contests.payments': 'Entry payments',
  'admin.contests.payouts': 'Payouts',
  'admin.contests.ledger': 'Ledger',
  'admin.contests.transitionDone': 'Done.',

  'admin.payments.title': 'Payments and payouts',
  'admin.payments.subtitle':
    'Verify entries, settle payments, and release prize and refund payouts.',
  'admin.payments.loadError': 'Could not load payments.',
  'admin.payments.entry': 'Entry',
  'admin.payments.provider': 'Provider',
  'admin.payments.reference': 'Reference',
  'admin.payments.settle': 'Record settlement',
  'admin.payments.settleAmount': 'Amount received',
  'admin.payments.settleNote': 'Note',
  'admin.payments.settleDone': 'Settlement recorded.',
  'admin.payments.resolve': 'Clear reconciliation flag',
  'admin.payments.resolveNote': 'What did you find?',
  'admin.payments.expire': 'Expire invoice',
  'admin.payments.flagged': 'Flagged for reconciliation',
  'admin.payouts.title': 'Payouts',
  'admin.payouts.empty': 'No payouts yet.',
  'admin.payouts.approve': 'Approve',
  'admin.payouts.process': 'Mark as processing',
  'admin.payouts.complete': 'Mark as paid',
  'admin.payouts.fail': 'Mark as failed',
  'admin.payouts.failReason': 'Why did it fail?',
  'admin.payouts.cancel': 'Cancel payout',
  'admin.payouts.createRefunds': 'Refund all confirmed entries',
  'admin.ledger.title': 'Ledger',
  'admin.ledger.balances': 'Balances by rail',
  'admin.ledger.note':
    'Balances are per rail and per asset; they are never summed across rails.',
  'admin.ledger.entryType': 'Type',
  'admin.ledger.direction': 'Direction',
  'admin.ledger.credits': 'In',
  'admin.ledger.debits': 'Out',
  'admin.ledger.net': 'Net',

  'api.contestNotFound': 'That contest does not exist.',
  'api.registrationClosed': 'Registration for this contest is closed.',
  'api.contestFull': 'This contest is full.',
  'api.notEnrolled': 'You are not entered in this contest.',
  'api.notActive': 'You are not currently taking part in this contest.',
  'api.entryNotConfirmed': 'Your entry has not been confirmed yet.',
  'api.contestNotLive': 'This contest is not open for participation.',
  'api.alreadyAnswered': 'You have already answered this question.',
  'api.submissionRejected': 'That answer was not accepted — the contest window is closed.',
  'api.answerInvalid': 'That answer is not valid for this question.',
  'api.resultsNotPublished': 'Results have not been published yet.',
  'api.rateLimited': 'Too many requests. Please wait a moment and try again.',
  'api.paymentNotFound': 'That payment does not exist.',
  'api.payoutNotFound': 'That payout does not exist.',
  'api.notFinalized': 'Finalize the contest before publishing its results.',
  'api.reasonRequired': 'A reason is required.',
  'api.idempotencyKeyReused': 'That request was already used for a different answer.',
  'api.contestNotEditable': 'A contest can only be edited while it is a draft.',
  'api.contentLocked': 'Contest content cannot be changed once the contest has started.',
  'api.invalidAmount': 'Enter a valid amount, e.g. 10.00',
  'api.assetRailMismatch': 'That asset does not belong to the selected payment rail.',
  'api.unknownAsset': 'That asset is not supported.',
  'api.invalidWindow': 'The registration and contest times are not in a valid order.',
  'api.contestHasNoQuestions': 'Add at least one question before scheduling the contest.',
  'api.questionWithoutAnswerKey': 'Every question needs a correct answer before scheduling.',
  'api.answerKeyRequired': 'Set the correct answer for this question.',
  'api.optionsRequired': 'A choice question needs at least two options.',
  'api.transitionContended': 'The contest changed while you were working. Reload and try again.',
  'api.invalidTransition': 'That lifecycle change is not allowed for this contest.',
  'api.notFlagged': 'That payment is not awaiting reconciliation.',

  // --- Crypto payment (USDT on-chain) --------------------------------------
  'pay.title': 'Pay with USDT',
  'pay.notIssued': 'No payment is required for this entry.',
  'pay.amount': 'Amount to send',
  'pay.network': 'Network',
  'pay.address': 'Deposit address',
  'pay.copy': 'Copy address',
  'pay.copied': 'Address copied',
  'pay.openInWallet': 'Open in wallet',
  'pay.received': 'Received',
  'pay.confirmations': 'Confirmations',
  'pay.txHash': 'Transaction',
  'pay.viewOnExplorer': 'View on the block explorer',
  'pay.expires': 'Pay before',
  'pay.checkNow': 'I have paid — check now',
  'pay.checking': 'Checking the blockchain…',
  'pay.autoCheck': 'We also check the blockchain automatically.',
  'pay.qrLabel': 'Deposit address QR code',
  'pay.scanHint': 'Scan with your wallet app, or copy the address.',
  'pay.awaitingHint':
    'Send exactly this amount of USDT on this network to the address below. The address is only for this entry.',
  'pay.detectedHint': 'We can see your transfer on the blockchain.',
  'pay.confirmingHint':
    'Your transfer is on the blockchain and is waiting for confirmations. Your entry activates automatically.',
  'pay.confirmedHint': 'Payment confirmed. You are entered in this contest.',
  'pay.overpaidHint':
    'Payment confirmed. You sent more than the entry fee; the surplus is recorded for review.',
  'pay.underpaidHint':
    'The amount received is below the entry fee. Send the remaining amount to the same address, or contact support.',
  'pay.expiredHint': 'This invoice has expired. Start the entry again to get a new address.',
  'pay.invalidHint':
    'We received a transfer we could not accept for this entry (wrong token or network). Contact support.',
  'pay.failedHint': 'This payment could not be matched to this entry. Contact support.',
  'pay.status.awaiting_payment': 'Awaiting payment',
  'pay.status.detected': 'Detected',
  'pay.status.confirming': 'Confirming',
  'pay.status.confirmed': 'Confirmed',
  'pay.status.underpaid': 'Underpaid',
  'pay.status.overpaid': 'Overpaid',
  'pay.status.expired': 'Expired',
  'pay.status.invalid': 'Not accepted',
  'pay.status.failed': 'Failed',

  // --- Legal ---------------------------------------------------------------
  'login.google': 'Continue with Google',
  'login.or': 'or',
  'login.staffNote': 'Signing in with a password is available for staff and existing accounts.',
  'login.googleError.cancelled': 'Google sign-in was cancelled. You can try again whenever you like.',
  'login.googleError.unavailable':
    'Google sign-in is not available on this deployment. Please use another method or contact support.',
  'login.googleError.accountConflict':
    'We could not sign you in with that Google account, because it is already connected to a different ITA6 account. Please contact support.',
  'login.googleError.accountInactive':
    'That ITA6 account is not active. Please contact support.',
  'login.googleError.failed': 'Sign-in with Google did not complete. Please try again.',
  'register.google': 'Sign up with Google',
  'admin.crypto.title': 'Crypto console',
  'admin.crypto.subtitle':
    'On-chain USDT: the deposit-address pool, invoices, observed transfers, payouts and reconciliation.',
  'admin.crypto.loadError': 'Could not load the crypto console.',
  'admin.crypto.rails': 'Payment rails',
  'admin.crypto.railsDisabled': 'disabled',
  'admin.crypto.networks': 'Networks and deposit addresses',
  'admin.crypto.available': 'available',
  'admin.crypto.unsupported': 'Not supported',
  'admin.crypto.pool': 'Deposit address pool',
  'admin.crypto.poolHint':
    'Every entry consumes one deposit address. When the pool is empty, entries are refused rather than given an address that cannot be attributed — import more well before that happens.',
  'admin.crypto.importNetwork': 'Network',
  'admin.crypto.importAsset': 'Token',
  'admin.crypto.poolFilter': 'Show',
  'admin.crypto.poolStatus.available': 'Available',
  'admin.crypto.poolStatus.assigned': 'Assigned',
  'admin.crypto.poolStatus.retired': 'Retired',
  'admin.crypto.import': 'Import public addresses',
  'admin.crypto.importPlaceholder': 'One address per line',
  'admin.crypto.importHint':
    'Public addresses only — never a private key. Each line is validated against the chain\u2019s real address format, and one bad entry rejects the whole import.',
  'admin.crypto.importButton': 'Import addresses',
  'admin.crypto.importEmpty': 'Paste at least one address.',
  'admin.crypto.imported': 'Imported {count} address(es). {available} now available.',
  'admin.crypto.address': 'Address',
  'admin.crypto.network': 'Network',
  'admin.crypto.status': 'Status',
  'admin.crypto.assignedTo': 'Assigned to',
  'admin.crypto.empty': 'Nothing to show.',
  'admin.crypto.runMonitor': 'Run chain monitor',
  'admin.crypto.monitorDone': 'Monitor finished: {examined} examined, {settled} settled.',
  'admin.crypto.signer': 'Payout signing',
  'admin.crypto.signerReady': 'Signer configured',
  'admin.crypto.signerMissing':
    'No signer configured — payouts cannot be broadcast automatically. They can still be sent by hand and the hash recorded.',
  'admin.crypto.payoutWallet': 'Payout wallet',
  'admin.crypto.payoutWalletMissing': 'Not configured',
  'admin.crypto.emulator': 'Chain emulator',
  'admin.crypto.emulatorOn': 'Enabled — rehearsal only, never production',
  'admin.crypto.emulatorOff': 'Disabled',
  'admin.crypto.emulatorQueued': '{count} queued observation(s)',
  'admin.crypto.stuck': 'Payments needing attention',
  'admin.crypto.recheck': 'Re-check now',
  'admin.crypto.recheckDone': 'Re-checked against the chain.',
  'admin.crypto.invoices': 'Crypto invoices',
  'admin.crypto.participant': 'Participant',
  'admin.crypto.expected': 'Expected',
  'admin.crypto.received': 'Received',
  'admin.crypto.confirmations': 'Confirmations',
  'admin.crypto.depositAddress': 'Deposit address',
  'admin.crypto.txHash': 'Transaction',
  'admin.crypto.amount': 'Amount',
  'admin.crypto.classification': 'Classified as',
  'admin.crypto.from': 'From',
  'admin.crypto.transfers': 'Observed on-chain transfers',
  'admin.crypto.reconciliation': 'Reconciliation',
  'admin.crypto.reconciliationHint':
    'What we recorded against what the chain says. A non-zero discrepancy is reported, never silently corrected.',
  'admin.crypto.applicationIn': 'Recorded in',
  'admin.crypto.applicationOut': 'Recorded out',
  'admin.crypto.observed': 'Observed on-chain',
  'admin.crypto.discrepancy': 'Discrepancy',
  'admin.crypto.notReadable': 'could not be read from the chain',
  'admin.crypto.unmatched': 'Unmatched on-chain transfers',
  'admin.crypto.payoutsHint': 'approve, sign and settle payouts there.',
  'admin.crypto.mode': 'Signing',
  'admin.crypto.mode.inWorker': 'in the Worker',
  'admin.crypto.mode.externalSigner': 'external signer',
  'admin.crypto.mode.manualOnly': 'manual only',
  'legal.privacy': 'Privacy Policy',
  'legal.terms': 'Terms and Conditions',
  'legal.lastUpdated': 'Last updated {date}',
  'legal.authoritativeNote':
    'This document is provided in several languages. If a translation differs from the English text, the English version is authoritative.',
} as const;
